import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapPin, Circle, EyeOff } from "lucide-react";

const ITEMS = [
  { key: "hideLocation", icon: MapPin, title: "Hide my location", on: "Location hidden", off: "Location visible" },
  { key: "hideOnlineStatus", icon: Circle, title: "Hide online status", on: "Online status hidden", off: "Online status visible" },
];

function ToggleRow({ icon: Icon, title, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <Icon className="h-5 w-5 shrink-0 text-slate-500" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800">{title}</p>
        {desc && <p className="mt-0.5 text-sm text-slate-500">{desc}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export function PrivacyControls() {
  const { user, refreshUser } = useAuth();
  const raw = user?.raw || {};
  const [state, setState] = useState({
    hideLocation: !!raw.hideLocation,
    hideOnlineStatus: !!raw.hideOnlineStatus,
    profileHidden: !!(raw.profileHidden ?? raw.hidden),
  });

  const update = async (key, v, msg) => {
    const prev = state[key];
    setState((s) => ({ ...s, [key]: v }));
    try { await api.put("/profile", { [key]: v }); await refreshUser(); toast.success(msg); }
    catch (err) { setState((s) => ({ ...s, [key]: prev })); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="privacy-controls">
      <div className="divide-y divide-emerald-900/5 rounded-2xl border border-emerald-900/10 bg-white shadow-sm">
        {ITEMS.map(({ key, icon, title, on, off }) => (
          <ToggleRow key={key} icon={icon} title={title} checked={state[key]} onChange={(v) => update(key, v, v ? on : off)} testid={`privacy-${key}`} />
        ))}
      </div>
      <div className="rounded-2xl border border-emerald-900/10 bg-white shadow-sm">
        <ToggleRow icon={EyeOff} title="Hide my profile" desc="Temporarily remove your profile from all discovery. Your account, matches and pending interests stay intact."
          checked={state.profileHidden} onChange={(v) => update("profileHidden", v, v ? "Profile hidden" : "Profile visible")} testid="privacy-profileHidden" />
      </div>
      {state.profileHidden && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-800" data-testid="privacy-hidden-note">
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Your profile is hidden. New members can't discover you until you turn this off. Existing matches are unaffected.</span>
        </div>
      )}
      <p className="text-sm text-slate-500">Contact sharing (email / phone / WhatsApp revealed only after a mutual match) is managed in Edit profile.</p>
    </div>
  );
}
